import { ProfilePictureLight } from '@/components/profile-picture/profile-picture.light';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { ChevronRight, Plus } from '@nosinovacao/fe-icons';
import {
  FloatingActionButton,
  If,
  mage,
  ImageProfile,
  useI18n,
} from '@nosinovacao/nosid-mfe-common';
import { type UsersProps } from '../-model/props';

export function UsersLight({ users, loading, onAddUser, onSelectUser }: Readonly<UsersProps>) {
  const { t } = useI18n();
  const hasUsers = users.length > 0;
  return (
    <div className="relative flex size-full min-h-0 flex-col overflow-y-auto pb-24 max-tablet:pb-28">
      <div className="flex flex-col gap-4">
        <span className="font-light text-nos-neutral-900 font-text-p1" data-tid="title-users-list">
          {t('Services.Users.ListTitle')}
        </span>
        <SkeletonLight loading={loading} className="h-[72px] w-full rounded" count={2}>
          <If condition={hasUsers}>
            <div className="grid gap-2">
              {users.map((user) => (
                <UserCard
                  key={user.Id}
                  name={user.DisplayName}
                  pictureUri={user.PamPictureUri}
                  isPending={user.State === 'Pending'}
                  onClick={() => onSelectUser(user.Id)}
                />
              ))}
            </div>
          </If>
          <If condition={!hasUsers}>
            <span className="text-nos-neutral-600 font-text-p2" data-tid="text-users-empty-state">
              {t('Services.Users.EmptyState')}
            </span>
          </If>
        </SkeletonLight>
      </div>
      <FloatingActionButton
        skin="light"
        className="fixed bottom-4 right-4"
        onClick={onAddUser}
        data-tid="button-add-user"
      >
        <Plus width={24} height={24} />
      </FloatingActionButton>
    </div>
  );
}

function UserCard({
  name,
  pictureUri,
  isPending,
  onClick,
}: {
  name: string;
  pictureUri: string | null;
  isPending: boolean;
  onClick: () => void;
}) {
  const { t } = useI18n();
  const image = mage.getImage(pictureUri, ImageProfile.App);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded border border-nos-neutral-200 bg-nos-neutral-0 p-4 text-start shadow-1"
      data-tid="button-select-user"
    >
      <ProfilePictureLight name={name} src={image} size={40} />
      <div className="flex flex-1 flex-col">
        <span className="text-nos-neutral-900 font-text-p1">{name}</span>
        <If condition={isPending}>
          <span className="text-nos-neutral-500 font-text-p2">{t('Services.Users.Pending')}</span>
        </If>
      </div>
      <ChevronRight width={20} height={20} className="shrink-0 text-nos-neutral-900" />
    </button>
  );
}
